// src/pages/partners/AddPartner.tsx
//
// AddPartner
// - Admin page to create a new partner.
// - Creates two credential sets:
//   1) Portal admin credentials (Admin ID / Password)
//   2) API credentials (App ID / App Secret)
// - Admin ID and Email must pass explicit duplication checks before creation.
//
// UX / guardrails
// - Single-column compact form (maxWidth=900).
// - Duplication checks are operator-driven gates (no auto-check).
// - Any change to Admin ID / Email resets the corresponding check status.
// - "Create Partner" is enabled only when:
//   - Form has been modified (dirty)
//   - Required fields are valid
//   - Duplication checks are both OK
//   - Not saving

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  CheckCircle,
  ContentCopy,
  ErrorOutline,
  Key,
  Refresh,
  Save,
  Search,
  Tune,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';

import { v4 as uuid } from 'uuid';
import { Buffer } from 'buffer';

import { copyToClipboard } from '@utils/common';
import type { PartnerForm } from '@models/partner';
import { createPartner, type PartnerCreateRequest } from '@apis/partner';
import { getUserByEmail, getUserByUsername } from '../../apis/users';

import { useConfirm } from '@components/common/confirm/confirm.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

type DuplicateCheckStatus = 'NOT_CHECKED' | 'DUPLICATED' | 'OK';

const normalize = (v?: string | null) => (v ?? '').trim();

/**
 * Generates a random credential string (uuid -> base64).
 * Note: convenience generator for operator workflow, not a security boundary.
 */
const makeBase64Uuid = () => Buffer.from(uuid()).toString('base64');
const makeShortPassword = () => makeBase64Uuid().slice(0, 8);

/**
 * Admin ID rule (system policy).
 * - a–z, 0–9, . _ -
 * - 4–30 chars
 */
const ADMIN_ID_RE = /^[a-z0-9._-]{4,30}$/;
const isValidAdminId = (v: string) => ADMIN_ID_RE.test(v);

/** Email format check (basic UX validation). */
const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const ACTION_BTN_SX = {
  whiteSpace: 'nowrap',
  width: { xs: '100%', sm: 160 },
  flex: { sm: '0 0 auto' },
} as const;

/**
 * Inline button style for actions next to TextField (Check / Regenerate).
 * - sm+: right side inline, aligned to input area
 * - xs: stacked below
 */
const INLINE_BTN_SX = {
  whiteSpace: 'nowrap',
  height: 40,
  width: { xs: '100%', sm: 180 },
  flex: { sm: '0 0 auto' },
  mt: { xs: 0, sm: 3 },
} as const;

const StatusChip = ({ status, label }: { status: DuplicateCheckStatus; label: string }) => {
  if (status === 'OK') {
    return <Chip size="small" color="success" icon={<CheckCircle />} label={`${label}: OK`} />;
  }
  if (status === 'DUPLICATED') {
    return <Chip size="small" color="error" icon={<ErrorOutline />} label={`${label}: Duplicated`} />;
  }
  return <Chip size="small" variant="outlined" label={`${label}: Not checked`} />;
};

const SectionHeader = ({
  icon,
  title,
  description,
}: {
  icon?: React.ReactNode;
  title: string;
  description?: string;
}) => {
  return (
    <Stack spacing={0.75} sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        {icon}
        <Typography variant="subtitle1" fontWeight={900} sx={{ minWidth: 0 }}>
          {title}
        </Typography>
      </Stack>

      {description ? (
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      ) : null}
    </Stack>
  );
};

const AddPartner = () => {
  const navigate = useNavigate();

  const confirm = useConfirm();
  const systemAlert = useSystemAlert();

  // -------------------- Form state --------------------
  const [form, setForm] = useState<PartnerCreateRequest>({
    name: '',
    managerUsername: '',
    managerPassword: makeShortPassword(),
    managerEmail: '',
    managerName: '',
    managerTelephone: '',
    address: '',
    remoteIps: [],
    appId: makeBase64Uuid(),
    appSecret: makeBase64Uuid(),
    callbackUrl: '',
    manualOrderEnabled: false,
  });

  const [isDirty, setIsDirty] = useState(false);

  // -------------------- Duplication checks --------------------
  const [usernameStatus, setUsernameStatus] = useState<DuplicateCheckStatus>('NOT_CHECKED');
  const [emailStatus, setEmailStatus] = useState<DuplicateCheckStatus>('NOT_CHECKED');

  const [checkingUsername, setCheckingUsername] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);

  // -------------------- UI state --------------------
  const [showPassword, setShowPassword] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);

  // -------------------- Field helpers --------------------
  const updateField = useCallback((name: keyof PartnerForm, value: string | boolean | number | null) => {
    setIsDirty(true);
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  /**
   * Any change to the checked fields invalidates the previous duplication result.
   */
  const onUsernameChanged = useCallback((value: string) => {
    setIsDirty(true);
    setUsernameStatus('NOT_CHECKED');
    setForm((prev) => ({ ...prev, managerUsername: value }));
  }, []);

  const onEmailChanged = useCallback((value: string) => {
    setIsDirty(true);
    setEmailStatus('NOT_CHECKED');
    setForm((prev) => ({ ...prev, managerEmail: value }));
  }, []);

  // -------------------- Derived (normalized values / validation) --------------------
  const partnerNameValue = normalize(form.name);

  const adminIdValue = normalize(form.managerUsername);
  const adminIdFormatInvalid = Boolean(adminIdValue) && !isValidAdminId(adminIdValue);

  const emailValue = normalize(form.managerEmail);
  const emailFormatInvalid = Boolean(emailValue) && !isValidEmail(emailValue);

  const managerNameValue = normalize(form.managerName);
  const telephoneValue = normalize(form.managerTelephone);
  const addressValue = normalize(form.address);
  const callbackUrlValue = normalize(form.callbackUrl);

  /**
   * Required fields for partner creation.
   * Note: remoteIps is optional in this page.
   */
  const hasRequiredValues = useMemo(() => {
    if (!partnerNameValue) return false;

    if (!adminIdValue) return false;
    if (adminIdFormatInvalid) return false;

    if (!managerNameValue) return false;

    if (!emailValue) return false;
    if (emailFormatInvalid) return false;

    if (!telephoneValue) return false;
    if (!addressValue) return false;

    if (!callbackUrlValue) return false;

    return true;
  }, [
    partnerNameValue,
    adminIdValue,
    adminIdFormatInvalid,
    managerNameValue,
    emailValue,
    emailFormatInvalid,
    telephoneValue,
    addressValue,
    callbackUrlValue,
  ]);

  /**
   * Final create gate:
   * - explicit operator intent (dirty)
   * - valid inputs
   * - explicit duplication checks OK
   * - not saving
   */
  const canCreate = useMemo(() => {
    if (saving) return false;
    if (!isDirty) return false;
    if (!hasRequiredValues) return false;
    if (usernameStatus !== 'OK') return false;
    if (emailStatus !== 'OK') return false;
    return true;
  }, [saving, isDirty, hasRequiredValues, usernameStatus, emailStatus]);

  const createDisabledReason = useMemo(() => {
    if (saving) return '';
    if (!isDirty) return 'No changes to save.';
    if (!hasRequiredValues) return 'Fill in all required fields.';
    if (usernameStatus !== 'OK') return 'Run Admin ID duplicate check.';
    if (emailStatus !== 'OK') return 'Run Email duplicate check.';
    return '';
  }, [saving, isDirty, hasRequiredValues, usernameStatus, emailStatus]);

  // -------------------- Duplicate checks --------------------
  const handleCheckUsernameDuplicate = useCallback(async () => {
    const username = normalize(form.managerUsername);

    if (!username) return systemAlert.info('Enter Admin ID first.');
    if (!isValidAdminId(username)) {
      return systemAlert.info('Invalid Admin ID. Use a-z, 0-9, and . _ - (4–30).');
    }

    try {
      setCheckingUsername(true);
      const { data } = await getUserByUsername(username);

      if ((data ?? []).length === 0) {
        setUsernameStatus('OK');
        systemAlert.success('Admin ID is available.');
      } else {
        setUsernameStatus('DUPLICATED');
        systemAlert.error('Admin ID is already in use.');
      }
    } catch {
      systemAlert.error('Failed to check Admin ID duplication.');
    } finally {
      setCheckingUsername(false);
    }
  }, [form.managerUsername, systemAlert]);

  const handleCheckEmailDuplicate = useCallback(async () => {
    const email = normalize(form.managerEmail);

    if (!email) return systemAlert.info('Enter Email first.');
    if (!isValidEmail(email)) return systemAlert.info('Enter a valid email address.');

    try {
      setCheckingEmail(true);
      const { data } = await getUserByEmail(email);

      if ((data ?? []).length === 0) {
        setEmailStatus('OK');
        systemAlert.success('Email is available.');
      } else {
        setEmailStatus('DUPLICATED');
        systemAlert.error('Email is already in use.');
      }
    } catch {
      systemAlert.error('Failed to check Email duplication.');
    } finally {
      setCheckingEmail(false);
    }
  }, [form.managerEmail, systemAlert]);

  // -------------------- Credential actions --------------------
  const handleRegeneratePassword = useCallback(() => {
    setIsDirty(true);
    setForm((prev) => ({ ...prev, managerPassword: makeShortPassword() }));
    systemAlert.info('Password regenerated.');
  }, [systemAlert]);

  const handleRegenerateAppId = useCallback(() => {
    setIsDirty(true);
    setForm((prev) => ({ ...prev, appId: makeBase64Uuid() }));
    systemAlert.info('App ID regenerated.');
  }, [systemAlert]);

  const handleRegenerateAppSecret = useCallback(() => {
    setIsDirty(true);
    setForm((prev) => ({ ...prev, appSecret: makeBase64Uuid() }));
    systemAlert.info('App Secret regenerated.');
  }, [systemAlert]);

  const handleCopy = useCallback(
    (value: string, message: string) => {
      copyToClipboard(value ?? '');
      systemAlert.success(message);
    },
    [systemAlert],
  );

  // -------------------- Create flow --------------------
  const handleCreate = useCallback(async () => {
    if (!canCreate || saving) return;

    try {
      setSaving(true);

      const payload: PartnerCreateRequest = {
        ...form,
        name: partnerNameValue,
        managerUsername: adminIdValue,
        managerEmail: emailValue,
        managerName: managerNameValue,
        managerTelephone: telephoneValue,
        address: addressValue,
        callbackUrl: callbackUrlValue,
      };

      await createPartner(payload);

      systemAlert.success('Partner created successfully.');
      navigate('/partners');
    } catch {
      systemAlert.error('Failed to create partner. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [
    form,
    canCreate,
    saving,
    partnerNameValue,
    adminIdValue,
    emailValue,
    managerNameValue,
    telephoneValue,
    addressValue,
    callbackUrlValue,
    systemAlert,
    navigate,
  ]);

  const handleOpenCreateConfirm = useCallback(async () => {
    // Operator-friendly guard messages (focus on the next missing requirement).
    if (!isDirty) return systemAlert.info('Make changes before creating.');
    if (!partnerNameValue) return systemAlert.info('Enter Partner Name.');

    if (!adminIdValue) return systemAlert.info('Enter Admin ID.');
    if (adminIdFormatInvalid) return systemAlert.info('Check Admin ID format.');

    if (!managerNameValue) return systemAlert.info('Enter Account Manager.');

    if (!emailValue) return systemAlert.info('Enter Email.');
    if (emailFormatInvalid) return systemAlert.info('Check Email format.');

    if (!telephoneValue) return systemAlert.info('Enter Telephone.');
    if (!addressValue) return systemAlert.info('Enter Address.');

    if (!callbackUrlValue) return systemAlert.info('Enter Callback URL.');

    if (usernameStatus === 'NOT_CHECKED') return systemAlert.info('Run Admin ID duplicate check.');
    if (usernameStatus === 'DUPLICATED') return systemAlert.error('Admin ID is duplicated.');

    if (emailStatus === 'NOT_CHECKED') return systemAlert.info('Run Email duplicate check.');
    if (emailStatus === 'DUPLICATED') return systemAlert.error('Email is duplicated.');

    const ok = await confirm({
      priority: 'info',
      title: 'Create partner?',
      description: (
        <>
          This will create a new partner and generate credentials for portal and API access.
          <br />
          <b>Admin ID</b> and <b>Email</b> must be verified as not duplicated.
        </>
      ),
      confirmText: 'Create',
      cancelText: 'Cancel',
      autoFocusConfirm: false,
    });

    if (ok) await handleCreate();
  }, [
    isDirty,
    partnerNameValue,
    adminIdValue,
    adminIdFormatInvalid,
    managerNameValue,
    emailValue,
    emailFormatInvalid,
    telephoneValue,
    addressValue,
    callbackUrlValue,
    usernameStatus,
    emailStatus,
    confirm,
    systemAlert,
    handleCreate,
  ]);

  const handleCancel = useCallback(async () => {
    if (saving) return;

    if (!isDirty) {
      navigate(-1);
      return;
    }

    const ok = await confirm({
      priority: 'info',
      title: 'Discard changes?',
      description: 'You have unsaved changes. If you leave now, those changes will be lost.',
      confirmText: 'Discard',
      cancelText: 'Stay',
      autoFocusConfirm: false,
    });

    if (ok) navigate(-1);
  }, [saving, isDirty, navigate, confirm]);

  return (
    <Box sx={{ py: 2, maxWidth: 900, width: '100%', mr: 'auto' }}>
      <Stack spacing={3} sx={{ minWidth: 0 }}>
        {/* Duplication check status summary */}
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <StatusChip status={usernameStatus} label="Admin ID" />
            <StatusChip status={emailStatus} label="Email" />
          </Stack>
        </Paper>

        {/* Portal account credentials */}
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Stack spacing={2.5}>
            <SectionHeader
              icon={<Tune fontSize="small" />}
              title="Account Information"
              description="Portal admin credentials are used for the partner portal. Admin ID and Email must be verified before creation."
            />

            <TextField
              label="Partner Name"
              value={form.name ?? ''}
              size="small"
              fullWidth
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="Enter partner name"
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems="stretch">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  label="Admin ID"
                  value={form.managerUsername ?? ''}
                  size="small"
                  fullWidth
                  onChange={(e) => onUsernameChanged(e.target.value)}
                  placeholder="e.g. partner_admin01"
                  error={usernameStatus === 'DUPLICATED' || adminIdFormatInvalid}
                  helperText={
                    adminIdFormatInvalid
                      ? 'Use a-z, 0-9, and . _ - (4–30).'
                      : usernameStatus === 'NOT_CHECKED'
                        ? 'Run duplicate check to continue.'
                        : usernameStatus === 'DUPLICATED'
                          ? 'This ID is already in use.'
                          : 'Available.'
                  }
                />
              </Box>

              <Button
                variant="outlined"
                size="small"
                startIcon={<Search />}
                onClick={handleCheckUsernameDuplicate}
                disabled={checkingUsername || !adminIdValue || adminIdFormatInvalid}
                sx={INLINE_BTN_SX}
              >
                {checkingUsername ? 'Checking…' : 'Check'}
              </Button>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems="stretch">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  label="Admin Password"
                  value={form.managerPassword ?? ''}
                  size="small"
                  fullWidth
                  disabled
                  type={showPassword ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={showPassword ? 'Hide' : 'Show'}>
                          <IconButton edge="end" size="small" onClick={() => setShowPassword((v) => !v)}>
                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Copy">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleCopy(form.managerPassword ?? '', 'Password copied.')}
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={handleRegeneratePassword} sx={INLINE_BTN_SX}>
                Regenerate
              </Button>
            </Stack>

            <TextField
              label="Account Manager"
              value={form.managerName ?? ''}
              size="small"
              fullWidth
              onChange={(e) => updateField('managerName', e.target.value)}
              placeholder="Enter manager name"
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems="stretch">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  label="Email"
                  value={form.managerEmail ?? ''}
                  size="small"
                  fullWidth
                  onChange={(e) => onEmailChanged(e.target.value)}
                  placeholder="e.g. admin@company.com"
                  error={emailStatus === 'DUPLICATED' || emailFormatInvalid}
                  helperText={
                    emailFormatInvalid
                      ? 'Enter a valid email address.'
                      : emailStatus === 'NOT_CHECKED'
                        ? 'Run duplicate check to continue.'
                        : emailStatus === 'DUPLICATED'
                          ? 'This email is already in use.'
                          : 'Available.'
                  }
                />
              </Box>

              <Button
                variant="outlined"
                size="small"
                startIcon={<Search />}
                onClick={handleCheckEmailDuplicate}
                disabled={checkingEmail || !emailValue || emailFormatInvalid}
                sx={INLINE_BTN_SX}
              >
                {checkingEmail ? 'Checking…' : 'Check'}
              </Button>
            </Stack>

            <TextField
              label="Telephone"
              value={form.managerTelephone ?? ''}
              size="small"
              fullWidth
              onChange={(e) => updateField('managerTelephone', e.target.value)}
              placeholder="Enter telephone number"
            />

            <TextField
              label="Address"
              value={form.address ?? ''}
              size="small"
              fullWidth
              onChange={(e) => updateField('address', e.target.value)}
              placeholder="Enter address"
            />

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(form.manualOrderEnabled)}
                    onChange={(_, checked) => updateField('manualOrderEnabled', checked)}
                  />
                }
                label="Manual Order (Portal)"
              />
              <Typography variant="body2" color="text.secondary">
                Enable this to allow the partner to create orders in the portal.
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* API credentials */}
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Stack spacing={2.5}>
            <SectionHeader
              icon={<Key fontSize="small" />}
              title="API Settings"
              description="App credentials are used for API authentication. They are generated by the system and can be regenerated."
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems="stretch">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  label="App ID"
                  value={form.appId ?? ''}
                  size="small"
                  fullWidth
                  disabled
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Copy">
                          <IconButton edge="end" size="small" onClick={() => handleCopy(form.appId ?? '', 'App ID copied.')}>
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={handleRegenerateAppId} sx={INLINE_BTN_SX}>
                Regenerate
              </Button>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems="stretch">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  label="App Secret"
                  value={form.appSecret ?? ''}
                  size="small"
                  fullWidth
                  disabled
                  type={showSecret ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={showSecret ? 'Hide' : 'Show'}>
                          <IconButton edge="end" size="small" onClick={() => setShowSecret((v) => !v)}>
                            {showSecret ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Copy">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleCopy(form.appSecret ?? '', 'App Secret copied.')}
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Button
                variant="outlined"
                size="small"
                startIcon={<Refresh />}
                onClick={handleRegenerateAppSecret}
                sx={INLINE_BTN_SX}
              >
                Regenerate
              </Button>
            </Stack>

            <TextField
              label="Callback URL"
              value={form.callbackUrl ?? ''}
              size="small"
              fullWidth
              onChange={(e) => updateField('callbackUrl', e.target.value)}
              placeholder="https://..."
            />
          </Stack>
        </Paper>

        {/* Bottom actions */}
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
          <Stack spacing={1.5}>
            <Alert severity="info">Admin ID and Email must be verified before creating a partner.</Alert>

            <Divider />

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.25}
              justifyContent="flex-end"
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <Button variant="outlined" onClick={handleCancel} disabled={saving} sx={ACTION_BTN_SX}>
                Cancel
              </Button>

              <Tooltip title={createDisabledReason} disableHoverListener={canCreate || !createDisabledReason}>
                <Box component="span" sx={{ width: { xs: '100%', sm: 160 }, flex: { sm: '0 0 auto' } }}>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleOpenCreateConfirm}
                    disabled={!canCreate || saving}
                    sx={ACTION_BTN_SX}
                  >
                    {saving ? 'Creating…' : 'Create Partner'}
                  </Button>
                </Box>
              </Tooltip>
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

export default AddPartner;
